import { useState, useEffect } from "react";

import { useAtom } from "jotai";

import store from "store2";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { projectPickerState } from "./JotaiAtoms";
import { refreshToken } from "./Auth";

import AddIcon from "@mui/icons-material/Add";
import FolderIcon from "@mui/icons-material/Folder";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Stack from "@mui/material/Stack";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import log from "./logger";
import * as u from "./utility";
import { useHttpApiPost } from "./hooks";

import { useParams } from "react-router-dom";

type NewProjectDialogProps = {
  open: boolean;
  onClose: (project?: string) => void;
};

function NewProjectDialog({ open, onClose }: NewProjectDialogProps) {
  const [project, setProject] = useState("");

  const handleClose = (newProject?: string) => {
    if (onClose) {
      onClose(newProject);
    }
    setProject("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProject(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && project.trim()) {
      handleClose(project);
    }
  };

  return (
    <Dialog
      onClose={() => handleClose()}
      open={open}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          minWidth: 400,
          p: 2,
        },
      }}
    >
      <DialogTitle sx={{ p: 0, mb: 2, fontSize: 20, fontWeight: 500 }}>
        Create new project
      </DialogTitle>

      <Stack gap={2}>
        <TextField
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          defaultValue={project}
          label="Project name"
          variant="outlined"
          fullWidth
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
            },
          }}
        />
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button
            onClick={() => handleClose()}
            sx={{
              textTransform: 'none',
              borderRadius: '20px',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleClose(project)}
            variant="contained"
            disabled={!project.trim()}
            sx={{
              textTransform: 'none',
              borderRadius: '20px',
              px: 3,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
            }}
          >
            Create
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
}

function ProjectPicker() {
  const [projects, setProjects] = useState<string[]>([]);
  const [project, setProject] = useAtom<string>(projectPickerState);
  const [selection, setSelection] = useState<string | number>("");
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const httpPost = useHttpApiPost();

  const { proj } = useParams();
  const updateContent = () => {
    const request = async () => {
      try {
        const response = await httpPost("admin", { action: "projects" });
        let projectList = response.data.projects;

        if (!u.equalArrays(projectList, projects)) {
          setProjects(projectList);
        } else {
          projectList = projects;
        }

        setProject((prev: string) => {
          if (prev && projectList.length) {
            const found = projectList.indexOf(prev);
            if (found !== -1) {
              setSelection(found);
              return prev;
            } else if (!proj) {
              setSelection(0);
              return projectList[0];
            }
          } else if (projectList.length && !proj) {
            setSelection(0);
            return projectList[0];
          } else if (proj) {
            setSelection("");
            return proj;
          }
          return prev;
        });
      } catch (err) {
      } finally {
      }
    };
    request();
  };

  useEffect(() => {
    updateContent();
  }, []);

  useEffect(() => {
    if (project && projects && projects.length > 0) {
      const found = projects.indexOf(project);
      if (found === -1) {
        updateContent();
      } else {
        setSelection(found);
      }
    }
  }, [project, projects]);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProjectSelect = (selectedProject: string) => {
    if (selectedProject === project) {
      handleMenuClose();
      return;
    }

    store("ProjectPicker:" + window.location.search, selectedProject);
    window.location.href = u.getProjectUrl(selectedProject);
  };

  const handleNewProjectClick = () => {
    handleMenuClose();
    setDialogOpen(true);
  };

  const handleDialogClose = (newProject?: string) => {
    const request = async (newProject: string) => {
      try {
        await httpPost("admin", { action: "grant", project: newProject });
        refreshToken(async () => {
          window.location.href = u.getProjectUrl(newProject);
        });
      } catch (err) {
        log.debug(err);
      } finally {
      }
    };

    setDialogOpen(false);

    if (newProject && newProject.length) {
      request(newProject);
    }
  };

  return (
    <>
      <Box
        onClick={handleMenuOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 2,
          py: 0.75,
          borderRadius: '20px',
          cursor: 'pointer',
          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
          backgroundColor: (theme) => theme.palette.mode === 'dark'
            ? 'rgba(232, 234, 237, 0.08)'
            : '#f1f3f4',
          '&:hover': {
            backgroundColor: (theme) => theme.palette.mode === 'dark'
              ? 'rgba(232, 234, 237, 0.12)'
              : '#e8eaed',
          },
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <FolderIcon sx={{ fontSize: '18px', color: (theme) => theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368', flexShrink: 0 }} />
        <Typography
          fontSize={14}
          fontWeight={500}
          sx={{
            color: (theme) => theme.palette.mode === 'dark' ? '#e8eaed' : '#202124',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {project || 'Select project'}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: '18px', color: (theme) => theme.palette.mode === 'dark' ? '#9aa0a6' : '#5f6368', flexShrink: 0 }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            maxWidth: 300,
            borderRadius: '8px',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.5)'
              : '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        {projects.map((proj) => (
          <MenuItem
            key={proj}
            onClick={() => handleProjectSelect(proj)}
            selected={proj === project}
            sx={{
              fontSize: 14,
              '&.Mui-selected': {
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(138, 180, 248, 0.16)'
                  : '#e8f0fe',
              },
            }}
          >
            {proj}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={handleNewProjectClick} sx={{ fontSize: 14 }}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>New project</ListItemText>
        </MenuItem>
      </Menu>

      <NewProjectDialog open={dialogOpen} onClose={handleDialogClose} />
    </>
  );
}

export default ProjectPicker;
